import {
  PLAYER_ACTIVITY_KIND_WORDS,
  PLAYER_ACTIVITY_OUTCOME_WORDS,
  PLAYER_ACTIVITY_REASON_WORDS,
  gamingDayOffset,
  type StaffActivityRow,
} from '@dayon/contracts';
import { formatCount } from '@dayon/money';
import { codeAsWords } from '../../lib/audit-words.ts';

/**
 * The Logs tab's pure decisions: the query it sends, and the words each row is read in. No hooks and
 * no markup, so they are tested as functions. A kind, an outcome or a reason never reaches a screen
 * as its code; a code this console has no words for yet is said as words by `codeAsWords`.
 */
export interface ActivityFilters {
  kind: string;
  outcome: string;
  from: string;
  to: string;
}

export const EMPTY_FILTERS: ActivityFilters = { kind: '', outcome: '', from: '', to: '' };

/** The longest stretch of an unrecognised browser string a row prints. */
const USER_AGENT_CUT = 120;

/** `kind, outcome, from, to, cursor, limit`, blanks left out, so the same filters give the same URL. */
export const activityQueryString = (
  f: ActivityFilters,
  cursor: string | null,
  limit: number,
): string => {
  const params = new URLSearchParams();
  if (f.kind) params.set('kind', f.kind);
  if (f.outcome) params.set('outcome', f.outcome);
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  if (cursor) params.set('cursor', cursor);
  params.set('limit', String(limit));
  return params.toString();
};

const KIND_WORDS: Readonly<Record<string, { done: string; tried: string }>> =
  PLAYER_ACTIVITY_KIND_WORDS;
const OUTCOME_WORDS: Readonly<Record<string, string>> = PLAYER_ACTIVITY_OUTCOME_WORDS;
const REASON_WORDS: Readonly<Record<string, string>> = PLAYER_ACTIVITY_REASON_WORDS;

/** What happened. Only a row that succeeded claims the act; every other outcome says it was tried. */
export const activityHeadline = (kind: string, outcome: string): string => {
  const words = KIND_WORDS[kind];
  if (!words) return codeAsWords(kind);
  return outcome === 'SUCCEEDED' ? words.done : words.tried;
};

export const outcomeWords = (outcome: string): string =>
  OUTCOME_WORDS[outcome] ?? codeAsWords(outcome);

/** The sentences under a row's headline. Never the HTTP status and never the route. */
export const detailWords = (row: StaffActivityRow): string[] => {
  const out: string[] = [];
  const { reason, by, sessionsEnded } = row.detail;
  if (reason) out.push(REASON_WORDS[reason] ?? codeAsWords(reason));
  if (by) out.push(`By staff (${by.role})`);
  if (typeof sessionsEnded === 'number') out.push(`Sessions ended: ${sessionsEnded}`);
  if (row.outcome === 'ERROR') out.push('Did not finish');
  /* A staff entry never held an address, though the retention job stamps it like every row. */
  if (row.redacted && !by) out.push('Address and browser removed after the retention period');
  if (row.requestId) out.push(`Reference ${row.requestId}`);
  return out;
};

export const deviceWords = (row: StaffActivityRow): string =>
  row.device ?? (row.userAgent ? `Not recognised: ${row.userAgent.slice(0, USER_AGENT_CUT)}` : '—');

export const exportFileName = (
  playerRef: string,
  o: { from: string; to: string; ref: string },
): string => `activity-log-${playerRef}-${o.from}-to-${o.to}-${o.ref}.csv`;

/** A range that ends before it starts, said in words; null when the days make sense. */
export const rangeProblem = (from: string, to: string): string | null =>
  from && to && from > to ? 'The range starts after it ends.' : null;

/** The default stretch an export covers when From is blank: this many days before To. */
const EXPORT_DEFAULT_DAYS = 30;

/**
 * The days an export covers. A blank To is today and a blank From is 30 days before To, so an
 * operator who sets only To still gets a range that makes sense. Manila gaming days throughout:
 * noon Manila time on To, stepped back, cannot land on the wrong calendar day.
 */
export const exportDays = (
  f: Pick<ActivityFilters, 'from' | 'to'>,
  now: Date = new Date(),
): { from: string; to: string } => {
  const to = f.to || gamingDayOffset(0, now);
  const from = f.from || gamingDayOffset(-EXPORT_DEFAULT_DAYS, `${to}T12:00:00+08:00`);
  return { from, to };
};

/**
 * The pop-up's title when the platform refuses a read. Only a refusal that names From or To is
 * about the days; anything else (a page the list did not issue, a filter it does not know) is not,
 * and "Check the days" would send the operator to fix something that is not wrong.
 */
export const readRefusalTitle = (fields: Record<string, string> | undefined): string =>
  fields && (Object.hasOwn(fields, 'from') || Object.hasOwn(fields, 'to'))
    ? 'Check the days'
    : 'The list could not continue';

/** The AMLA floor and the default, in days: the only period this console calls five years. */
const FIVE_YEARS_IN_DAYS = 1825;

/** How long addresses are kept, in words: "five years" at the default, otherwise the days. */
export const retentionWords = (days: number): string =>
  days === FIVE_YEARS_IN_DAYS ? 'five years' : `${formatCount(days)} days`;

/** A page joined to the list: the first page replaces it, a later one is appended. */
export const mergePage = (
  prev: StaffActivityRow[],
  items: StaffActivityRow[],
  after: string | null,
): StaffActivityRow[] => (after ? [...prev, ...items] : items);
